<template>
  <div class="mit-spaces-map-edit">
    <BaseMitLoading class="mit-progress" :show="loading" :overlayFullscreen="false"/>
    <v-dialog v-model="saveDialog" width="400">
        <v-card>
            <v-card-title v-if="saveResults.success" class="text-h5 primary lighten-2">
             Saved
            </v-card-title>
            <v-card-title v-else class="text-h5 red lighten-2">
             Error
            </v-card-title>

          <v-card-text class="pt-5">
                <div v-if="saveResults.success">
                    Successfully saved {{saveResults.successNum}} map space/object(s).
                    <div v-if="saveResults.navSaved" class="mt-3 font-weight-medium green--text text--darken-2">
                        Navigation data save!
                    </div>
                </div>
                <div v-else>
                    Failed to save {{saveResults.failedNum}} item(s)
                </div>
                <div v-if="saveResults.failedMessage" class="mt-2">{{saveResults.failedMessage}}</div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="closeSaveDialog">Close</v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>

    <v-card flat class="map-container-card">

        <div class="search-container d-flex justify-end">
            <v-card class="d-flex align-center search-card" rounded elevation="2">
                <v-autocomplete class="pt-0 mt-0 flex-grow-1 search-input" clearable dense solo flat hide-details :filter="autocompleteSearch" v-model="search" :items="searchItems" label="Search" item-text="mitIdShort" return-object @change="autocompleteChanged">
                    <!-- <template v-slot:prepend-inner>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" class="mr-2" color="grey darken-2">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Search for things like Rooms, bathrooms, printers, phone booths, Lab PIs and more...</span>
                        </v-tooltip>
                    </template> -->
                    <template v-slot:item="data">
                        <template v-if="data.item.isNeighborhood">
                            <!-- Added special group icon -->
                            <v-list-item-avatar>
                                <v-icon color="primary">mdi-account-group</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>
                                    <!-- Smart suffix to avoid "Tidor Group Group" -->
                                    <span>{{ data.item.name }}{{ data.item.name.toLowerCase().includes('group') ? '' : ' Group' }}</span>
                                    <span class="grey--text text--darken-1 text-caption ml-1">
                                        (Main Space: {{ data.item.mitId }})<span v-if="data.item.piList && data.item.piList.length"> - PI {{ Array.isArray(data.item.piList) ? data.item.piList.join(', ') : data.item.piList }}</span>
                                    </span>
                                </v-list-item-title>
                            </v-list-item-content>
                        </template>
                        <template v-else-if="data.item && data.item.isTypeGroup">
                            <v-list-item-avatar>
                                <v-icon>mdi-shape-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold" v-html="data.item.name"></v-list-item-title>
                                <!-- Removed "Type / Group", replaced with specific category label -->
                                <v-list-item-subtitle class="primary--text caption">{{ data.item.useType }} Category</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                        <template v-else-if="data.item && typeof data.item.spaceType === 'floor'">
                            <v-list-item-content v-text="'Floor '+data.item.level"></v-list-item-content>
                        </template>
                        <template v-else-if="data.item">
                            <v-list-item-avatar>
                            <v-icon>mdi-office-building-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <div v-if="data.item.name && data.item.name.length > 0">
                                    <v-list-item-title v-html="data.item.name"></v-list-item-title>
                                    <v-list-item-title class="text-caption grey--text text--darken-1" v-html="data.item.mitIdShort"></v-list-item-title>
                                    <!-- Subtext that clearly identifies the type/use -->
                                    <v-list-item-subtitle class="primary--text caption" v-html="data.item.useType"></v-list-item-subtitle>
                                </div>
                                <div v-else>
                                    <v-list-item-title v-html="data.item.mitIdShort"></v-list-item-title>
                                    <!-- Subtext that clearly identifies the type/use -->
                                    <v-list-item-subtitle class="primary--text caption" v-html="data.item.useType"></v-list-item-subtitle>
                                </div>
                            </v-list-item-content>
                        </template>
                        </template>
               </v-autocomplete>

               <v-divider vertical></v-divider>

               <v-select dense solo flat hide-details style="max-width: 120px;" class="pt-0 mt-0" label="Floor" :items="floors" item-text="name" item-value="val" v-model="selectedFloor" @change="changeFloor()"></v-select>
            </v-card>
        </div>

        <div class="scrollable">
            <svg id="mit-svg"></svg>
        </div>
        
        <div class="toolbar-drawing-left" v-if="editMode">
             <v-btn v-if="!drawing" dark class="mx-2 mb-2" fab color="primary" @click="startDrawing()">
                <v-icon>mdi-pencil-outline</v-icon>
             </v-btn>
             <v-card v-else elevation="2" max-height="60vh" class="d-flex flex-column">
                <v-tabs v-model="activeTab" @change="drawMap" background-color="grey lighten-1" show-arrows height="40" style="flex-shrink:0;">
                    <v-tab>Objects</v-tab>
                    <v-tab class="d-none">Structures</v-tab>
                    <v-tab>Navigation</v-tab>
                </v-tabs>
                <v-tabs-items v-model="activeTab" style="overflow-y: auto;">
                    <v-tab-item>
                        <v-card-text class="pa-0">
                            <v-list dense>
                                <v-list-item-group color="primary" v-model="palletObjectSelected" @change="palletObjectChanged(palletObjects)">
                                    <v-list-item class="mr-0 ml-0 pl-0" v-for="(obj, index) in palletObjects" :key="index" :value="obj.id">
                                        <v-list-item-icon class="mr-0 ml-0 pl-0">
                                          <v-img contain max-height="30" max-width="60" :src="obj.iconPath"></v-img>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                          <v-list-item-title>{{obj.name}}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                           </v-list>
                        </v-card-text>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card-text class="pa-0">
                             <v-list dense>
                                <v-list-item-group color="primary" v-model="palletObjectSelected" @change="palletObjectChanged(structuresPallet)">
                                    <v-list-item class="mr-0 ml-0 pl-0" v-for="(obj, index) in structuresPallet" :key="index" :value="obj.id">
                                        <v-list-item-icon class="mr-0 ml-0 pl-0">
                                          <v-icon>{{obj.icon}}</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                          <v-list-item-title>{{obj.name}}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                           </v-list>
                        </v-card-text>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card-text class="pa-0">
                             <v-list dense>
                                <v-list-item-group color="primary" v-model="palletObjectSelected" @change="palletObjectChanged(navigationPallet)">
                                    <v-list-item class="mr-0 ml-0 pl-0" v-for="(obj, index) in navigationPallet" :key="index" :value="obj.id">
                                        <v-list-item-icon class="mr-0 ml-0 pl-0">
                                          <v-icon :color="obj.color || ''">{{obj.icon}}</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                          <v-list-item-title>{{obj.name}}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                           </v-list>
                        </v-card-text>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </div>

        <div class="toolbar-zoom-controls">
            <v-btn dark class="zoom-in-btn mb-1" fab small color="grey darken-2"><v-icon>mdi-plus</v-icon></v-btn>
            <v-btn dark class="zoom-out-btn mb-1" fab small color="grey darken-2"><v-icon>mdi-minus</v-icon></v-btn>
            <v-btn dark class="zoom-reset-btn mb-1" fab small color="grey darken-2"><v-icon>mdi-restart</v-icon></v-btn>
            <v-btn v-if="editMode" dark class="download-svg-btn" fab small color="primary" title="Export Map to SVG" @click="downloadSVG()"><v-icon>mdi-download</v-icon></v-btn>
        </div>

        <div class="toolbar-drawing-center">
            <v-card v-if="drawing" elevation="2">
              <v-card-subtitle dark class="primary white--text lighten-1 pa-2">
                Editing
              </v-card-subtitle>
              <v-card-text class="pa-0">
                  <v-btn text small color="" @click="save()">
                      <v-icon>mdi-content-save</v-icon>
                      Save
                  </v-btn>
                  <v-btn text small color="" @click="cancel()">
                      <v-icon>mdi-cancel</v-icon>
                      Cancel
                  </v-btn>
              </v-card-text>
           </v-card>
        </div>
        
        <div class="toolbar-drawing-right">
            <v-card v-if="selectedSpace" elevation="2" class="d-flex flex-column space-props-card overflow-hidden">
              <v-card-title class="pa-0 ma-0" style="flex-shrink: 0;">
                  <v-spacer></v-spacer> <v-btn text class="mx-0" fab small @click="closeProperties()">
                      <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
              </v-card-title>

              <div style="overflow-y: auto; flex-grow: 1; min-height: 0;">
                  <v-card-text v-if="!drawing" class="pa-4 pt-0 mt-0">
                    <div class="text-h5 font-weight-bold mb-1">{{selectedSpace.properties.name || selectedSpace.properties.mitId}}</div>
                    <div class="text-subtitle-1 primary--text mb-4">{{selectedSpace.properties.useType}} <span class="grey--text">| {{selectedSpace.properties.mitId}}</span></div>

                     
                    <div class="mb-4">
                        <v-btn outlined color="primary" block elevation="0" @click="navigateToThisSpace">
                            <v-icon left>mdi-navigation-variant</v-icon> Navigate to this
                        </v-btn>
                    </div>

                    <div v-if="selectedSpace.properties.useType === 'QR_POINT'" class="mb-4">
                        <v-btn color="primary" block dark @click="openPrintableQrCode">
                            <v-icon left>mdi-qrcode</v-icon> Print QR Poster
                        </v-btn>
                    </div>
                    <div v-if="getSpaceGroups(selectedSpace.properties.mitId).length > 0" class="mb-4">
                        <div class="text-overline mb-2">Groups</div>
                        <v-card outlined class="pa-2 mb-2 d-flex align-center" v-for="g in getSpaceGroups(selectedSpace.properties.mitId)" :key="g.id" style="border-radius: 8px;">
                            <v-avatar size="16" :color="g.color" class="mr-3"></v-avatar>
                            <div>
                                <div class="font-weight-bold text-body-2">{{g.name}}</div>
                                <div v-if="g.piList" class="text-caption grey--text">PI: {{g.piList}}</div>
                            </div>
                            <v-spacer></v-spacer>
                            <v-chip v-if="g.headSpaceId === selectedSpace.properties.mitId" x-small color="primary" outlined>Main Space</v-chip>
                        </v-card>
                    </div>

                    <v-divider class="mb-3"></v-divider>
                    
                    <div v-if="selectedSpace.properties.description" class="mb-3">
                        <div class="text-caption grey--text text--darken-1">Description</div>
                        <div class="text-body-2">{{selectedSpace.properties.description}}</div>
                    </div>

                    <v-row dense class="mb-3">
                        <v-col cols="6" v-if="selectedSpace.properties.spaceType == 'space'">
                            <div class="text-caption grey--text text--darken-1">Organization</div>
                            <div class="text-body-2">{{selectedSpace.properties.org || 'N/A'}}</div>
                        </v-col>
                        <v-col cols="6" v-if="selectedSpace.properties.spaceType == 'space'">
                            <div class="text-caption grey--text text--darken-1">Area</div>
                            <div class="text-body-2">{{selectedSpace.properties.sqfoot || '0'}} sqft</div>
                        </v-col>
                        <v-col cols="6" v-if="selectedSpace.properties.personCapacity">
                            <div class="text-caption grey--text text--darken-1">Capacity</div>
                            <div class="text-body-2">{{selectedSpace.properties.personCapacity}} people</div>
                        </v-col>
                        <v-col cols="6" v-if="selectedSpace.properties.seating">
                            <div class="text-caption grey--text text--darken-1">Seating</div>
                            <div class="text-body-2">{{selectedSpace.properties.seating}}</div>
                        </v-col>
                    </v-row>

                    <div v-if="selectedSpace.properties.equipment && selectedSpace.properties.equipment.length > 0" class="mb-3">
                        <div class="text-caption grey--text text--darken-1">Equipment</div>
                        <div class="text-body-2">{{selectedSpace.properties.equipment}}</div>
                    </div>

                    <div v-if="selectedSpace.properties.images && selectedSpace.properties.images.length > 0">
                        <div class="text-caption grey--text text--darken-1 mb-2">Images</div>
                        <v-row class="pa-1">
                            <v-img @click="dialogImages = true;" class="ma-1 rounded" v-for="(image, index) in selectedSpace.properties.images" :key="index" max-width="130px" :src="'/images/'+image" style="cursor: pointer; border: 1px solid #eee;"></v-img>
                        </v-row>
                    </div>
                    
                    <v-dialog v-model="dialogImages" max-width="800px">
                        <v-card elevation="24">
                            <v-toolbar dense color="grey lighten-1">
                                <b>{{selectedSpace.properties.mitId}}</b><span class="pl-1">Images</span><v-spacer></v-spacer><v-btn color="grey lighten-1" elevation="0" fab small @click="dialogImages=false"><v-icon>mdi-close-circle-outline</v-icon></v-btn>
                            </v-toolbar>
                            <v-carousel>
                                <v-carousel-item
                                v-for="(item,i) in selectedSpace.properties.images"
                                :key="i"
                                :src="'/images/'+item"
                                reverse-transition="fade-transition"
                                transition="fade-transition"
                                ></v-carousel-item>
                            </v-carousel>
                        </v-card>
                    </v-dialog>
                </v-card-text>
              <div v-if="drawing">
                <div class="text-h5 font-weight-bold mb-1 pl-3">{{selectedSpace.properties.name || selectedSpace.properties.mitId}}</div>
                <div class="text-subtitle-1 primary--text mb-4 pl-3">{{selectedSpace.properties.useType}} <span class="grey--text">| {{selectedSpace.properties.mitId}}</span></div>
                 <v-btn v-if="selectedSpace.properties.spaceType === 'object'" class="ma-2 mb-4" text outlined color="blue darken-2" @click="changeSize(1.1)">+size</v-btn>
                 <v-btn v-if="selectedSpace.properties.spaceType === 'object'" class="ma-2 mb-4" text outlined color="blue darken-2" @click="changeSize(.9)">-size</v-btn>
                 <v-text-field v-if="selectedSpace.properties.spaceType === 'object'" style="max-width:100px;" class="pa-2 pt-0" outlined dense hide-details min="-360" max="360" v-model.number="selectedSpace.properties.rotation" type="number" label="Rotation" @change="rotate"></v-text-field>
                 
                 <div v-if="selectedSpace.properties.spaceType === 'nav_point'">
                     <v-text-field dense hide-details outlined class="pa-2 mt-2" filled readonly v-model="selectedSpace.properties.mitId" label="ID"></v-text-field>
                     
                     <v-select
                       v-if="selectedSpace.properties.useType === 'LEVEL_CHANGE'"
                       dense hide-details outlined class="pa-2 mt-2"
                       :items="['Stairs', 'Elevator']"
                       v-model="selectedSpace.properties.levelChangeType"
                       label="Type (Routing Option)"
                       @change="editedSelectedSpaceProperties"
                     ></v-select>
                     
                     <v-text-field 
                       dense hide-details outlined class="pa-2 mt-2"
                       v-model="selectedSpace.properties.name" 
                       label="Name (Optional)"
                       @change="editedSelectedSpaceProperties"
                     ></v-text-field>

                     <div v-if="selectedSpace.properties.useType === 'QR_POINT'" class="pa-2 mt-2">
                         <v-btn color="primary" block dark @click="openPrintableQrCode">
                             <v-icon left>mdi-qrcode</v-icon> Print QR Poster
                         </v-btn>
                     </div>

                     <div v-if="selectedSpace.properties.useType === 'NAV_ROOM_ENTRANCE'" class="pa-2 mt-2" style="background-color: #f5f5f5; border-radius: 4px;">
                        <div class="text-body-2 mb-3">
                            <b>Associated Space:</b> {{ selectedSpace.properties.mitId.split('_entrance_')[0] }}
                        </div>
                        <v-switch
                            v-model="selectedSpace.properties.isPrimary"
                            label="Primary Entrance"
                            hide-details
                            dense
                            class="mt-0"
                            @change="editedSelectedSpaceProperties"
                        ></v-switch>
                        <div class="text-caption grey--text mt-2">
                            <b>Note:</b> If you drag this point to a new location, it will automatically link to the closest room when you click Save.
                        </div>
                    </div>

                     <v-textarea 
                       rows="2" dense hide-details outlined class="pa-2 mt-2" 
                       v-model="selectedSpace.properties.navTip" 
                       label="Navigation Tip"
                       @change="editedSelectedSpaceProperties"
                     ></v-textarea>

                     <v-text-field 
                       dense hide-details outlined class="pa-2 mt-2"
                       v-model="selectedSpace.properties.imageUrl" 
                       label="Image URL"
                       @change="editedSelectedSpaceProperties"
                     ></v-text-field>
                     
                     <div v-if="selectedSpace.properties.useType === 'LEVEL_CHANGE'" class="pa-2">
                         <div class="subtitle-2 font-weight-bold mb-2 mt-2">Access to Levels</div>
                            <v-checkbox
                                v-for="lvl in floors"
                                :key="lvl.val"
                                v-model="selectedSpace.properties.accessToLevels"
                                :label="lvl.name"
                                :value="lvl.val"
                                hide-details
                                dense
                                class="mt-0"
                                @change="editedSelectedSpaceProperties"
                            ></v-checkbox>
                        </div>
                    </div>
                    <div v-if="selectedSpace.properties.spaceType === 'nav_line'">
                        <v-text-field 
                            dense hide-details outlined class="pa-2 mt-2" 
                            filled readonly 
                            v-model="selectedSpace.properties.mitId" 
                            label="ID">
                        </v-text-field>
                    </div>
                 
                 <template v-if="selectedSpace.properties.spaceType !== 'nav_point' && selectedSpace.properties.spaceType !== 'nav_line'">
                     <v-text-field dense hide-details outlined class="pa-2" filled readonly v-model="selectedSpace.properties.mitId" label="ID"></v-text-field>
                     <v-autocomplete
                       v-if="selectedSpace.properties.spaceType === 'space'"
                       class="pa-2 pb-0"
                       dense
                       outlined
                       hide-details
                       multiple
                       small-chips
                       :items="groups"
                       item-text="name"
                       item-value="id"
                       v-model="selectedGroupIds"
                       label="Assigned Groups"
                       clearable
                       @change="onSpaceGroupChange"
                     ></v-autocomplete>
                     
                     <v-select
                       v-if="selectedSpace.properties.spaceType === 'space'"
                       class="pa-2 pb-0"
                       dense
                       outlined
                       hide-details
                       :items="['Default', 'Common Area', 'Stairs or Elevator', 'Custom']"
                       v-model="selectedColorMode"
                       label="Space Color Mode"
                       @change="onColorModeChange"
                     ></v-select>
                     <v-color-picker
                       v-if="selectedColorMode === 'Custom' && selectedSpace.properties.spaceType === 'space'"
                       v-model="selectedSpace.properties.color"
                       @input="onColorChange"
                       hide-inputs
                       class="ma-2"
                     ></v-color-picker>
                     
                     <v-divider ></v-divider>
                     
                     <v-text-field 
                       dense hide-details outlined
                       @focus="editNameEnter"
                       @blur="editNameLeave"
                       @change="editedSelectedSpaceProperties" class="pl-2 pr-2 pt-3" v-model="selectedSpace.properties.name" label="Name"
                     ></v-text-field>
                     
                     <v-textarea rows="3" counter maxlength="300" outlined @change="editedSelectedSpaceProperties" class="pa-2 mt-2 pb-0" v-model="selectedSpace.properties.description" label="Description"></v-textarea>
                     <v-textarea rows="3" class="pa-2 pt-3 pb-0" counter maxlength="400" outlined @change="editedSelectedSpaceProperties"  v-model="selectedSpace.properties.equipment" label="Equipment" hint="Equipment in this space, useful for reservable spaces"></v-textarea>
                     <v-text-field class="pa-2 pt-0" outlined dense hide-details min="0" max="10000" v-model.number="selectedSpace.properties.personCapacity" type="number" label="Max. Person Capacity"  @change="editedSelectedSpaceProperties" hint="Max number of people"></v-text-field>
                     <v-text-field class="pa-2 pt-0 pb-3" dense hide-details outlined @change="editedSelectedSpaceProperties" v-model="selectedSpace.properties.seating" label="Seating" hint="Seating in this space"></v-text-field>
                    <v-divider ></v-divider>
                    
                    <v-row no-gutters>
                        <v-col cols="9">
                            <v-file-input :loading="selectedSpaceImageToUploadLoading" class="pt-3" dense v-model="selectedSpaceImageToUpload" accept="image/*" label="Primary Image" hint="320px X 320px or 320px X 640px or less in size" persistent-hint ></v-file-input>
                        </v-col>
                        <v-col cols="3">
                            <v-btn class="mt-3" :disabled="selectedSpaceImageToUpload == null" color="primary lighten-2" small @click="uploadSpaceImage(selectedSpace.properties.id, selectedSpaceImageToUpload)">Upload</v-btn>
                        </v-col>
                    </v-row>
                    <v-row no-gutters v-for="(image, index) in selectedSpace.properties.images" :key="index">
                            <v-col class="pa-0" cols="9">
                                <v-text-field dense hide-details outlined class="pa-2 pr-0" filled readonly v-model="selectedSpace.properties.images[index]" label="Image"></v-text-field>
                            </v-col>
                            <v-col cols="3">
                                <v-btn class="mt-2 ml-2" color="red lighten-2" fab small @click="deleteSpaceImage(selectedSpace.properties.images[index], index)"><v-icon>mdi-delete-outline</v-icon></v-btn>
                            </v-col>
                    </v-row>
                 </template>
                 
                 <v-divider class="mt-2 mb-2"></v-divider>
                 
                 <v-card-text class="pt-0">
                      <v-btn small color="red" width="100%" @click="deleteSelectedObject()">
                          <v-icon>mdi-delete</v-icon>
                          Delete
                      </v-btn>
                  </v-card-text>
             </div>
             </div>
            </v-card>
        </div>

       <div class="toolbar-map-types">
           <v-btn dark fab color="primary" elevation="2" @click="mapTypesSheet = true">
               <v-icon>mdi-layers</v-icon>
           </v-btn>
       </div>

       <v-bottom-sheet v-model="mapTypesSheet">
           <v-sheet class="text-left pa-4 rounded-t-xl" height="400">
               
               <!-- Top Section: Full Width -->
               <div class="text-h6 mb-2">Map View Settings</div>
               <v-radio-group v-model="mapType" @change="drawMap" row>
                   <v-radio label="Default" value="Default"></v-radio>
                   <v-radio label="Structures" value="Structures"></v-radio>
               </v-radio-group>
               
               <v-divider class="my-3"></v-divider>

               <!-- Bottom Section: Two Columns -->
               <v-row>
                   <!-- Left Column: Details -->
                   <v-col cols="12" md="6">
                       <div class="text-subtitle-1 font-weight-bold mb-2">Details</div>
                       
                       <v-switch v-model="showCsailOnly" label="Show CSAIL Only Spaces" hide-details @change="onCsailOnlyChange"></v-switch>
                       <v-switch v-model="showAllObjects" label="Show all objects" hide-details @change="drawMap"></v-switch>
                       <v-switch v-model="showNeighborhoods" label="Show Groups" hide-details @change="drawMap"></v-switch>
                       <v-switch v-model="showConferenceRooms" label="Show Conference Rooms" hide-details @change="drawMap"></v-switch>
                       <v-switch v-if="editMode" v-model="showNavigation" label="Show navigation paths" hide-details @change="drawMap"></v-switch>
                       <v-switch v-if="editMode && showNavigation" v-model="showEntrances" label="Show room entrances" hide-details @change="drawMap"></v-switch>
                   </v-col>

                   <!-- Right Column: Export Controls -->
                   <v-col cols="12" md="6" v-if="editMode">
                       <div class="text-subtitle-1 font-weight-bold mb-2">Room Number Controls</div>
                       
                       <v-switch v-model="roomNumberOptions.enabled" label="Custom Space Numbers" hide-details @change="drawMap"></v-switch>
                       
                       <div v-if="roomNumberOptions.enabled" class="ml-4 mt-2">
                           <v-row align="center" dense>
                               <v-col cols="auto">
                                   <v-btn icon small @click="roomNumberOptions.size--; drawMap()"><v-icon>mdi-minus</v-icon></v-btn>
                               </v-col>
                               <v-col cols="4">
                                   <v-text-field v-model.number="roomNumberOptions.size" type="number" label="Size (pt)" hide-details dense outlined @change="drawMap"></v-text-field>
                               </v-col>
                               <v-col cols="auto">
                                   <v-btn icon small @click="roomNumberOptions.size++; drawMap()"><v-icon>mdi-plus</v-icon></v-btn>
                               </v-col>
                           </v-row>
                           
                           <v-row align="center" dense class="mt-2">
                               <v-col cols="6">
                                   <v-checkbox v-model="roomNumberOptions.bold" label="Bold Font" hide-details class="mt-0 pt-0" @change="drawMap"></v-checkbox>
                               </v-col>
                               <v-col cols="6">
                                   <v-text-field v-model="roomNumberOptions.color" type="color" label="Font Color" hide-details dense outlined @change="drawMap"></v-text-field>
                               </v-col>
                           </v-row>

                           <v-checkbox v-model="roomNumberOptions.removeBg" label="Remove White Background" hide-details class="mt-2 pt-0" @change="drawMap"></v-checkbox>
                       </div>
                   </v-col>
               </v-row>
               
           </v-sheet>
        </v-bottom-sheet>

     </v-card>
  </div>
</template>

<script>
import AnalyticsTracker from '../services/MitAnalyticsTracker.js';
import BaseMitLoading from '../../base/components/BaseMitLoading.vue';
import MitMapService from '../services/MitMapService.js';
import MitSpacesService from '../services/MitSpacesService.js';
import MitSpacesSettingsService from '../services/MitSpacesSettingsService.js';
import MitQueryParamService from '../services/MitQueryParamService.js';
import MitMapLegendService from '../services/MitMapLegendService.js';
import MitMapLockService from '../services/MitMapLockService.js';

export default {
  name: 'MitSpacesMap2',
  components: { BaseMitLoading },
  props: {
      buildingMitId: { default:null, type:String },
      editMode: { default:false, type:Boolean }
  },
  data: function() {
    return {
        loading:true, dialogImages:false, drawing:false,
        saveDialog:false, 
        saveResults: { success: true, total: 0, successNum: 0, failedNum: 0, failedMessage: null, navSaved: false },
        objectsModel:null, spacesList:[], spacesModel: {}, groups: [],
        activeTab: 0, doorsModel: null, windowsModel: null, structuresModel: null,
        info:{ levels:[], spaceTypes:[], spaceUses:[], orgs:[], floorDict:{} },
        showMenuObjects:true, existingObjectMitIdsList:{}, editNameBeforeChanges:'',
        floors:[], selectedFloor:0, selectedType:null,
        selectedOrg: null, selectedSpace:null,
        selectedSpaceImageToUpload:null, selectedSpaceImageToUploadLoading:false,
        selectedColorMode: 'Default', selectedGroupIds: [],
        selectedGroupId: null, 
        palletObjectSelected:null, mapType: 'Default', //OR default to "Structures"
        showAllObjects: false, showNeighborhoods: true, showNavigation: false, mapTypesSheet: false,
        showCsailOnly: false, showEntrances: false,
        showConferenceRooms: true,
        roomNumberOptions: {
            enabled: false,
            size: 4,
            removeBg: false,
            bold: false, // <-- New
            color: '#000000' // <-- New
        },
        saveObjects: { created:[], modified:[], deleted:[] }, 
        navFeaturesModel: null,                               
        deletedNavFeatures: [],                               
        
        existingNavMitIdsList: {},    

        palletObjects:[
            { id:'PRINTER', width:10, height:10, imagePath:'/assets/objects/PRINTER.png', icon:'mdi-printer', name:'Printer' },
            { id:'PHONEBOOTH', width:15, height:15, imagePath:'/assets/objects/PHONEBOOTH.png', icon:'mdi-card-account-phone', name:'Phonebooth' },
            { id:'HELPDESK', width:15, height:15, imagePath:'/assets/objects/HELPDESK.png', icon:'mdi-account-wrench', name:'Help Desk' },
            { id:'AASTATION', width:15, height:15, imagePath:'/assets/objects/AASTATION.png', icon:'mdi-account-group', name:'AA Station' },
            { id:'CARDREADER', width:5, height:5, imagePath:'/assets/objects/CARDREADER.png', icon:'mdi-card-account-details', name:'Card Reader' }
        ],
        structuresPallet: [
            { id: 'STAIRS', type: 'STAIRS', name: 'Stairs', icon: 'mdi-stairs', width:20, height:20 },
            { id: 'ELEVATOR', type: 'ELEVATOR', name: 'Elevator', icon: 'mdi-elevator', width:20, height:20 }
        ],
        navigationPallet: [
            { id: 'PATH', type: 'PATH', name: 'Path', icon: 'mdi-vector-line', color: 'darkred' },
            { id: 'END_POINT', type: 'END_POINT', name: 'End Point', icon: 'mdi-circle', color: 'red' },
            { id: 'LEVEL_CHANGE', type: 'LEVEL_CHANGE', name: 'Level Change Point', icon: 'mdi-circle-outline', color: 'red' },
            { id: 'QR_POINT', type: 'QR_POINT', name: 'QR Location Point', icon: 'mdi-qrcode', color: 'blue' },
            { id: 'CONNECTION_POINT', type: 'CONNECTION_POINT', name: 'Connection Point', icon: 'mdi-source-commit', color: 'grey' },
            { id: 'NAV_ROOM_ENTRANCE', type: 'NAV_ROOM_ENTRANCE', name: 'Room Entrance', icon: 'mdi-door-open', color: 'black' }
        ],
        svg:null, size:{ w:1000, h:800 }, zoom:null, search:null, mapSpaceSelected:'',
        colorCodes:{ BACKGROUND:'#DDC7A8', GREY:'#838382', 'CORRIDOR':'#DDC7A8', 'STAIRWAY':'#838382', 'ELEVATOR':'#838382' },
        mapService:null, mitQueryParamService: new MitQueryParamService(),
        settingsService: new MitSpacesSettingsService(), legendService: null
    }
  },
  computed: {
    // The one selection the map shows; group wins over space, space over type (same as MitMapService)
    selectionKind() {
        if (this.selectedGroupId) return 'group';
        if (this.mapSpaceSelected) return 'space';
        if (this.selectedType) return 'type';
        return null;
    },
    searchItems() {
      let items = [];
      if (this.groups) {
          this.groups.forEach(g => {
              if (g.headSpaceId && g.spaceIds && g.spaceIds.length > 0) {
                  let headSpace = this.spacesList ? this.spacesList.find(s => s.mitId === g.headSpaceId) : null;
                  
                  items.push({
                      isNeighborhood: true,
                      id: g.id, // <-- Add the group ID here
                      mitId: g.headSpaceId,
                      mitIdShort: g.name,
                      name: g.name,
                      piList: g.piList,
                      useType: 'Group',
                      level: headSpace ? headSpace.level : null 
                  });
              }
          });
      }
      if (this.info && this.info.spaceTypes) {
        this.info.spaceTypes.forEach(type => { items.push({ isTypeGroup: true, mitIdShort: type, name: `All ${type}s`, useType: type }); });
      }
      if (this.spacesList) { items = items.concat(this.spacesList); }
      return items;
    }
  },
  watch: { 'buildingMitId' (to, from) { this.init(); } },
  mounted() {
      this.legendService = new MitMapLegendService("#mit-svg");
      this.getObjectDefinitions()
      .then( (data)=> {
          this.mapService = new MitMapService("#mit-svg", this.palletObjects, this.selectedSpaceChanged, null, this.editedObject);
          this.init();
      })
      .catch((error) => {
          this.mapService = new MitMapService("#mit-svg", this.palletObjects, this.selectedSpaceChanged, null, this.editedObject);
          this.init();
      });
  },
  // Release lock if user navigates away from the page while in edit mode
  beforeDestroy() {
          if (this.drawing) {
              MitMapLockService.releaseLock(this.buildingMitId);
          }
  },
  methods : {
      init(){
          this.loading = true;
          this.mapService.clearMap();
          
          Promise.all([ this.getSpacesList(), this.getBuildingInfo(), this.getGroups(), this.getBuildingData() ])
          .then(() => {
              this.initMap();
              this.loading = false;
          }).catch((error) => {
              console.log('An error occurred during initialization', error);
              this.loading = false;
          });
      },
      navigateToThisSpace() {
            const mitId = this.selectedSpace.properties.mitId;
            const building = this.buildingMitId;
            
            // Push to the directions page with building and end target populated. 
            // The directions page will leave 'start' blank, asking the user where they are starting from.
            this.$router.push({ path: '/directions', query: { b: building, end: mitId } });
      },
      getGroups() {
          return MitSpacesService.getGroups().then(data => {
              this.groups = data;
              if (this.mapService) { this.mapService.setGroups(this.groups); }
          }).catch(err => console.log('Error fetching groups', err));
      },
      getSpaceGroups(mitId) {
          if(!this.groups) return [];
          return this.groups.filter(g => g.spaceIds && g.spaceIds.includes(mitId));
      },
      initMap(){
          this.floors.splice(0, this.floors.length);
          for(let x = 0; x < this.info.levels.length; x++){
             this.floors.push({name:'Floor '+this.info.levels[x], val:parseInt(this.info.levels[x])});
          }

          let defaultFloor = this.mitQueryParamService.get('l');
          let defaultSelectedSpace = this.mitQueryParamService.get('s');

          if(defaultFloor){ this.selectedFloor = parseInt(defaultFloor); }
          else{
              if(this.floors.length > 3){
                if(this.buildingMitId == 45){
                    this.selectedFloor = this.floors[7].val;
                    this.floors[5].name += ' (CSAIL)'; this.floors[6].name += ' (CSAIL)'; this.floors[7].name += ' (CSAIL)';
                } else if (this.buildingMitId == 32){
                    this.selectedFloor = this.floors[2].val;
                }
              }
          }

          // Restore a shared selection from the URL (?g= group, ?s= space)
          let defaultGroupId = this.mitQueryParamService.get('g');
          let defaultItem = null;
          if (defaultGroupId) {
              defaultItem = this.searchItems.find(i => i.isNeighborhood && String(i.id) === String(defaultGroupId)) || null;
          } else if (defaultSelectedSpace) {
              defaultItem = this.spacesList.find(s => s.mitId === defaultSelectedSpace) || null;
          }

          if (defaultItem && defaultItem.isNeighborhood) {
              this.selectedGroupId = defaultItem.id;
              this.mapSpaceSelected = defaultItem.mitId;
          } else if (defaultSelectedSpace) {
              this.mapSpaceSelected = defaultSelectedSpace;
          }
          if (defaultItem) {
              this.search = defaultItem;
              if (!defaultFloor && defaultItem.level) { this.selectedFloor = parseInt(defaultItem.level); }
          }

          this.mapService.setEditMode(true);
          this.mapService.setHighlightMouseOver(true);
          this.mapService.enableZoom(".zoom-in-btn", ".zoom-out-btn", ".zoom-reset-btn");

          this.drawMap();
          if (this.selectionKind === 'space') { this.mapService.select(this.mapSpaceSelected, false); }
          if (this.selectionKind) { this.mapService.zoomToSelection(); }
      },
      getObjectDefinitions(){ return this.settingsService.getObjectDefinitions(true).then((data) => { this.palletObjects = data.definitions; }); },
      getSpacesList(){
          return MitSpacesService.getSpacesList(this.buildingMitId)
              .then( (data)=> { this.spacesList = data.spaces; })
              .catch( (error)=> { console.log('An error occurred', error); })
      },
      getBuildingInfo(){
          return MitSpacesService.getBuildingStats(this.buildingMitId)
              .then( (data)=> {
                  this.info.levels = data.stats.levels; this.info.spaceTypes = data.stats.useType;
                  this.info.spaceUse = data.stats.useCategory; this.info.orgs = data.stats.orgs;
                  this.info.floorDict = data.floors;
                  if(this.buildingMitId == 45){ 
                      this.selectedOrg = 'SCHWARZMAN COLLEGE OF COMPUTING'; 
                  }
                  else if (this.buildingMitId == 32){ 
                      this.selectedOrg = 'COMP SCI & AI LAB'; 
                      this.showCsailOnly = true; 
                  } else {
                      this.selectedOrg = null;
                      this.showCsailOnly = false;
                  }
              })
              .catch( (error)=> { console.log('An error occurred', error); });
      },
      getBuildingData(geojsonTypes, structureTypes){
          geojsonTypes = geojsonTypes || 'space,door,object';
          structureTypes = structureTypes || 'window,floor';

          return new Promise ((resolve, reject) => {
              let reqGeoJson = MitSpacesService.getBuildingGeoJson(this.buildingMitId, {types:geojsonTypes});
              let reqStruct = (structureTypes.length > 0) ? MitSpacesService.getBuildingCoordinates(this.buildingMitId, {types:structureTypes}) : Promise.resolve(null);
              let reqNav = MitSpacesService.getNavigation(this.buildingMitId);

              Promise.all([reqGeoJson, reqStruct, reqNav]).then((values) => {
                  let mapModelStruct = values[1] || {};
                  let mapModelGeo = this.mapService.convertGeoJSONtoMapModel(values[0]);

                  this.doorsModel = mapModelGeo.door;
                  this.windowsModel = mapModelStruct.window;
                  this.structuresModel = mapModelStruct.floor;
                  this.spacesModel = mapModelGeo.space;
                  this.objectsModel = mapModelGeo.object;
                  this.navFeaturesModel = values[2] && values[2].features ? values[2].features : [];
                  resolve();
              })
              .catch( (error)=> { reject(error); });
          });
      },

      editedObject(eventType, space) {
        // ADD 'NAV_ROOM_ENTRANCE' to this array
        const navTypes = ['nav_point', 'nav_line', 'nav_path', 'PATH', 'LEVEL_CHANGE', 'END_POINT', 'CONNECTION_POINT', 'QR_POINT', 'NAV_ROOM_ENTRANCE'];
        const isNav = navTypes.includes(space.properties.spaceType) || navTypes.includes(space.properties.useType);
        
        if (isNav) {
            if (!space.properties.spaceType) {
                space.properties.spaceType = space.geometry.type === 'LineString' ? 'nav_line' : 'nav_point';
            }
            this.editedNavObject(eventType, space);
        } else {
            this.editedSpaceObject(eventType, space);
        }
      },

      editedNavObject(eventType, space) {
          if (!this.navFeaturesModel) this.navFeaturesModel = [];

          if (eventType === this.mapService.EDIT_OBJECT_TYPE.CREATED || eventType === this.mapService.EDIT_OBJECT_TYPE.MODIFIED) {
              
              const existingIdx = this.navFeaturesModel.findIndex(f => f.properties.mitId === space.properties.mitId);
              if (existingIdx === -1) {
                  this.navFeaturesModel.push(space);
              } else {
                  this.navFeaturesModel.splice(existingIdx, 1, space);
              }
              
              this.existingNavMitIdsList[space.properties.mitId] = 1; 
          } 
          else if (eventType === this.mapService.EDIT_OBJECT_TYPE.DELETED) {
              const existingIdx = this.navFeaturesModel.findIndex(f => f.properties.mitId === space.properties.mitId);
              if (existingIdx > -1) {
                  this.navFeaturesModel.splice(existingIdx, 1);
              }
              this.deletedNavFeatures.push(space);
              
              delete this.existingNavMitIdsList[space.properties.mitId]; 
          }
          
          if (this.navFeaturesModel) {
              this.navFeaturesModel = [...this.navFeaturesModel];
          }
      },

      editedSpaceObject(eventType, space) {
          let createdIdx = this.saveObjects.created.indexOf(space);
          let modifiedIdx = this.saveObjects.modified.indexOf(space);

          switch(eventType){
              case this.mapService.EDIT_OBJECT_TYPE.CREATED:
                  if (space.properties.spaceType === 'object') {
                      this.objectsModel[space.properties.level].features.push(space);
                  } 
                  if (createdIdx === -1) this.saveObjects.created.push(space);
                  this.existingObjectMitIdsList[space.properties.mitId] = 1;
              break;
              
              case this.mapService.EDIT_OBJECT_TYPE.MODIFIED:
                  if (createdIdx === -1 && modifiedIdx === -1) {
                      this.saveObjects.modified.push(space);
                  }
              break;
              
              case this.mapService.EDIT_OBJECT_TYPE.DELETED:
                  if(createdIdx > -1){
                      this.saveObjects.created.splice(createdIdx, 1);
                  } else if(modifiedIdx > -1){
                      this.saveObjects.modified.splice(modifiedIdx, 1);
                      this.saveObjects.deleted.push(space);
                  } else {
                      this.saveObjects.deleted.push(space);
                  }

                  if (space.properties.spaceType === 'object') {
                      if (this.objectsModel && this.objectsModel[space.properties.level]) {
                          let objArray = this.objectsModel[space.properties.level].features;
                          let idx = objArray.indexOf(space);
                          if (idx > -1) objArray.splice(idx, 1);
                      }
                  }
                  delete this.existingObjectMitIdsList[space.properties.mitId];
              break;
          }
      },

      editedSelectedSpaceProperties(event){ this.editedObject(this.mapService.EDIT_OBJECT_TYPE.MODIFIED, this.selectedSpace); },
      
      async save() {
            this.drawing = false;
            this.palletObjectSelected = null;
            this.mapService.changeCreateObject(null);
            this.mapService.setEditModelAllowModify(false);
            this.loading = true;
            this.saveResults = { success: true, total: 0, successNum: 0, failedNum: 0, failedMessage: null, navSaved: false };

            try {
                // Tell the service to save any internal legend modifications
                if (this.legendService) {
                    await this.legendService.saveAllModifications(this.buildingMitId);
                }

                // Capture both the space count and the nav success boolean
                const [spacesCount, navSavedFlag] = await Promise.all([
                    this.saveSpacesData(),
                    this.saveNavigationData()
                ]);
                
                // Release lock upon successful save
                await MitMapLockService.releaseLock(this.buildingMitId);
                
                this.saveResults.successNum = spacesCount;
                this.saveResults.navSaved = navSavedFlag; // Pass the boolean to the UI
                
                this.loading = false;
                this.clearSaveObjects();
                this.saveDialog = true;
            } catch (error) {
                console.error('An error occurred during concurrent save', error);
                this.loading = false;
                this.clearSaveObjects();
                this.saveResults.success = false;
                
                if (error && error.errorCode === 'LOCK_OVERRIDDEN') {
                    this.saveResults.failedMessage = error.message; 
                } else {
                    this.saveResults.failedMessage = 'An error occurred during the save, see logs for details.';
                }
                
                this.saveDialog = true;
            }
      },

      async saveSpacesData() {
          let count = 0;
          let spacesSaveList = this.saveObjects.created.concat(this.saveObjects.modified);
          spacesSaveList = MitSpacesService.convertGeoJSONListtoSpaceModel(spacesSaveList);
          let spacesDeleteList = MitSpacesService.convertGeoJSONListtoSpaceModel(this.saveObjects.deleted);

          if (spacesDeleteList.length > 0) {
              let res = await MitSpacesService.deleteSpaces(spacesDeleteList);
              count += res.numSuccess || 0;
          }
          if (spacesSaveList.length > 0) {
              let res = await MitSpacesService.saveSpaces(spacesSaveList);
              count += res.numSuccess || 0;
          }
          return count;
      },

      async saveNavigationData() {
        let saved = false;
        if ((this.navFeaturesModel && this.navFeaturesModel.length > 0) || this.deletedNavFeatures.length > 0) {
            await MitSpacesService.saveNavigation({
                upsert: this.navFeaturesModel || [],
                delete: this.deletedNavFeatures || []
            });
            saved = true;
        }
        return saved;
      },

      clearSaveObjects(){ 
          this.saveObjects = { created:[], modified:[], deleted:[] }; 
          this.deletedNavFeatures = [];
          this.existingNavMitIdsList = {};

          // Tell the service to clear tracked modifications
           if (this.legendService) {
               this.legendService.clearModifications();
           }
      },
      
      closeSaveDialog(){
          this.saveDialog = false;
          this.loading = true;
          this.getBuildingData('space,door,object', '')
              .then( (data)=> {
                  this.drawMap();
                  this.loading = false;
              }).catch( (error)=> { console.log('An error occurred', error); this.loading = false; });
      },
      cancel(){
          this.clearSaveObjects();
          this.drawing = false;
          this.palletObjectSelected = null;
          this.mapService.changeCreateObject(null);
          this.mapService.setEditModelAllowModify(false);

          // Release lock when aborting
          MitMapLockService.releaseLock(this.buildingMitId);

          this.loading = true;
          this.getBuildingData('space,door,object', '')
              .then( (data)=> {
                  this.drawMap();
                  this.loading = false;
              }).catch( (error)=> { console.log('An error occurred', error); this.loading = false; });
      },

      changeSize(amount){ this.mapService.changeSizeSelectedObject(amount); },
      rotate(angle){ this.mapService.rotateSelectedObject(angle); },
      async startDrawing() {
          this.loading = true;
          
          try {
              // 1. Attempt to acquire the building lock
              let lockStatus = await MitMapLockService.acquireLock(this.buildingMitId);
              
              // 2. Handle the Social Override Prompt
              if (lockStatus.locked) {
                  // Calculate minutes since locked
                  const lockTime = new Date(lockStatus.lockedAt);
                  const minsAgo = Math.round((new Date() - lockTime) / 60000);
                  
                  const override = window.confirm(`Sorry, ${lockStatus.lockedBy} is editing this building and started ${minsAgo} minutes ago.\n\nWould you like to cancel their edit?`);
                  
                  if (override) {
                      await MitMapLockService.acquireLock(this.buildingMitId, true);
                  } else {
                      this.loading = false;
                      return; // User declined, abort drawing mode
                  }
              }

              // 3. Lock acquired, safely proceed to drawing mode
              let existingObjectMitIdsList = await MitSpacesService.getExistingBuildingObjectMitIds(this.buildingMitId || '32');
              this.drawing = true;
              
              this.existingNavMitIdsList = {};
              if (this.navFeaturesModel) {
                  this.navFeaturesModel.forEach(f => {
                      if (f.properties && f.properties.mitId) {
                          this.existingNavMitIdsList[f.properties.mitId] = 1;
                      }
                  });
              }

              this.mapService.setEditModelAllowModify(true, existingObjectMitIdsList, this.existingNavMitIdsList);
              this.existingObjectMitIdsList = existingObjectMitIdsList;
              this.renderActiveLegend();
          } catch (error) {
              console.error('Lock acquisition failed', error);
              alert("Failed to access map editor: " + error.message);
          } finally {
              this.loading = false;
          }
      },
      deleteSelectedObject(){
          this.mapService.deleteSelectedObject();
          this.closeProperties()
      },
      
    onColorModeChange(mode) {
        if (mode === 'Default') {
            this.selectedSpace.properties.color = null;
            this.selectedSpace.properties.commonArea = false;
            this.selectedSpace.properties.stairElevator = false;
        } else if (mode === 'Common Area') {
            this.selectedSpace.properties.color = '#E5D5BE';
            this.selectedSpace.properties.commonArea = true;
            this.selectedSpace.properties.stairElevator = false;
        } else if (mode === 'Stairs or Elevator') {
            this.selectedSpace.properties.color = '#A5A5A4';
            this.selectedSpace.properties.commonArea = false;
            this.selectedSpace.properties.stairElevator = true;
        } else if (mode === 'Custom') {
            if (!this.selectedSpace.properties.color || this.selectedSpace.properties.color === '#E5D5BE' || this.selectedSpace.properties.color === '#A5A5A4') {
                this.selectedSpace.properties.color = '#FFFFFF';
            }
            this.selectedSpace.properties.commonArea = false;
            this.selectedSpace.properties.stairElevator = false;
        }
        this.editedSelectedSpaceProperties();
        this.updateMapSpaceColor();
    },
    onColorChange(color) {
        if (typeof color === 'object' && color.hex) { this.selectedSpace.properties.color = color.hex; } 
        else { this.selectedSpace.properties.color = color; }
        this.editedSelectedSpaceProperties();
        this.updateMapSpaceColor();
    },
    updateMapSpaceColor() {
        if (this.mapService && this.selectedSpace) { this.mapService.updateSpaceColor(this.selectedSpace); }
    },
    onSpaceGroupChange(newGroupIds) {
          let spaceId = this.selectedSpace.properties.mitId;
          newGroupIds = newGroupIds || [];
          let promises = [];

          this.groups.forEach(g => {
              if (!g.spaceIds) g.spaceIds = [];
              let isAssigned = g.spaceIds.includes(spaceId);
              let shouldBeAssigned = newGroupIds.includes(g.id);

              if (isAssigned && !shouldBeAssigned) {
                  g.spaceIds = g.spaceIds.filter(id => id !== spaceId);
                  promises.push(MitSpacesService.saveGroup(g));
              } else if (!isAssigned && shouldBeAssigned) {
                  g.spaceIds.push(spaceId);
                  promises.push(MitSpacesService.saveGroup(g));
              }
          });

          if (newGroupIds.length > 0 && this.selectedSpace.properties.color) {
               this.selectedColorMode = 'Default';
               this.selectedSpace.properties.color = null;
               this.selectedSpace.properties.commonArea = false;
               this.selectedSpace.properties.stairElevator = false;
               this.editedSelectedSpaceProperties();
          }

          if (promises.length > 0) {
              Promise.all(promises).then(() => {
                  MitSpacesService.getGroups().then(data => {
                      this.groups = data;
                      if (this.mapService) {
                          this.mapService.setGroups(this.groups);
                          this.updateMapSpaceColor(); 
                      }
                  });
              }).catch(err => { console.error("Error updating space neighborhood assignment:", err); });
          }
      },
    editNameEnter(event){}, editNameLeave(event){},
    changeFloor(){
        this.mitQueryParamService.add('l',this.selectedFloor);
        
        // ABANDON PATH: If they change floors, force the pallet to deselect,
        // which should tell the drawing service to abort.
        if (this.drawing && this.activeTab === 2) {
            this.palletObjectSelected = null;
            this.mapService.changeCreateObject(null);
            // Note: Make sure MitMapService calls navDrawingService.cancelNavDrawing() 
            // when changeCreateObject(null) is fired.
        }
        
        this.drawMap();
    },
    selectedSpaceChanged(space){
          if(space){
              this.selectedSpace = space;
              this.mitQueryParamService.add('s',space.properties.mitId);
              this.mitQueryParamService.add('l',space.properties.level);
              this.mitQueryParamService.add('b',space.properties.mitBuildingId);

              // Clicking a room on the map makes it the selection, same as searching for it
              if (space.properties.spaceType === 'space') {
                  this.selectedGroupId = null;
                  this.selectedType = null;
                  this.mapSpaceSelected = space.properties.mitId;
                  this.search = this.spacesList.find(s => s.mitId === space.properties.mitId) || null;
                  this.mitQueryParamService.remove(['g']);
                  this.refreshSelection();
              }

              let currentGroups = this.getSpaceGroups(space.properties.mitId);
              this.selectedGroupIds = currentGroups.map(g => g.id);

              if (space.properties.color) {
                  if (space.properties.color === '#E5D5BE') { this.selectedColorMode = 'Common Area'; } 
                  else if (space.properties.color === '#A5A5A4') { this.selectedColorMode = 'Stairs or Elevator'; } 
                  else { this.selectedColorMode = 'Custom'; }
              } else if (space.properties.commonArea) { this.selectedColorMode = 'Common Area'; } 
              else if (space.properties.stairElevator || space.properties.stairsElevator) { this.selectedColorMode = 'Stairs or Elevator'; } 
              else { this.selectedColorMode = 'Default'; }
          }
          else{
              this.selectedSpace = null;
              this.mitQueryParamService.remove(['s']);

              // Closing a single space clears it; a group/type selection stays
              if (this.selectionKind === 'space') {
                  this.mapSpaceSelected = '';
                  this.search = null;
                  this.refreshSelection();
              }
          }
      },
    // Re-apply the selection without redrawing the map
    refreshSelection(){
        this.mapService.setSelection(this.selectedType, this.mapSpaceSelected, this.selectedGroupId);
        this.renderActiveLegend();
    },
    legendItemClicked(legendType, item){
        let isActive = legendType === 'conference'
            ? (this.selectionKind === 'space' && this.mapSpaceSelected === item.mitId)
            : (this.selectedGroupId === item.id);

        // Clicking the active row again clears it, like clearing the search box
        if (isActive) {
            this.search = null;
            this.selectItem(null, 'legend');
            return;
        }

        let searchItem;
        if (legendType === 'conference') {
            searchItem = this.spacesList.find(s => s.mitId === item.mitId)
                || { mitId: item.mitId, mitIdShort: item.mitId, name: item.name, useType: item.useType, level: this.selectedFloor, spaceType: 'space' };
        } else {
            searchItem = this.searchItems.find(i => i.isNeighborhood && i.id === item.id)
                || { isNeighborhood: true, id: item.id, mitId: item.headSpaceId || null, mitIdShort: item.name, name: item.name, useType: 'Group', level: null };
        }
        this.search = searchItem;
        this.selectItem(searchItem, 'legend');
    },
    legendItemHovered(legendType, item){
        if (!item) { this.mapService.previewIds(null); return; }
        this.mapService.previewIds(legendType === 'conference' ? [item.mitId] : (item.spaceIds || []));
    },
    closeProperties(){ this.mapService.deselect(); },
    autocompleteSearch(item, queryText, itemText) {
        if(!item) return false;
        const textMitIdShort = (item.mitIdShort || '').toLowerCase();
        const textUse = (item.useType || '').toLowerCase();
        const textName = (item.name || '').toLowerCase();
        const searchText = (queryText || '').toLowerCase();

        let piMatch = false;
        if (item.piList && Array.isArray(item.piList)) {
            piMatch = item.piList.some(pi => pi.toLowerCase().includes(searchText));
        }

        return textMitIdShort.includes(searchText) || 
               textUse.includes(searchText) || 
               textName.includes(searchText) || 
               piMatch;
    },
    autocompleteChanged(item) {
        this.selectItem(item, 'search');
    },
    // Single entry point for search and legend selections. Group visibility is left alone:
    // the selection spotlight (fade + outline) isolates the selection on its own.
    selectItem(item, source) {
        // Close any open space first; its deselect callback clears the old space selection
        this.mapService.deselect();

        this.selectedGroupId = null;
        this.selectedType = null;
        this.mapSpaceSelected = '';
        this.mitQueryParamService.remove(['s', 'g']);

        if (item) {
            if (source === 'legend') {
                AnalyticsTracker.track('search', 'search_legend', {
                    type: item.isNeighborhood ? 'group' : 'space',
                    id: item.isNeighborhood ? item.id : item.mitId
                });
            } else {
                // Analytics Metric 6: General Search
                AnalyticsTracker.track('search', 'search_general', {
                    query: item.name || item.mitIdShort || item.mitId,
                    context: 'map'
                });
            }

            if (item.isTypeGroup) {
                this.selectedType = item.useType;
            }
            else if (item.isNeighborhood) {
                // Analytics Metric 3: Search Group
                if (source !== 'legend') AnalyticsTracker.track('search', 'search_group', { group_id: item.id, name: item.name });

                this.mapSpaceSelected = item.mitId;
                this.selectedGroupId = item.id;
                this.mitQueryParamService.add('g', item.id);
            }
            else if (item.mitId) {
                if (source !== 'legend') {
                    if (item.spaceType === 'object') {
                        // Analytics Metric 4: Search Object
                        AnalyticsTracker.track('search', 'search_object', { object_id: item.mitId, type: item.useType });
                    } else {
                        // Analytics Metric 5: Search Space
                        AnalyticsTracker.track('search', 'search_space', { space_id: item.mitId, level: item.level });
                    }
                }
                this.mapSpaceSelected = item.mitId;
            }

            if (!item.isTypeGroup && item.level) {
                this.selectedFloor = parseInt(item.level);
                this.mitQueryParamService.add('l', this.selectedFloor);
            }
        }

        this.drawMap();

        if (item && !item.isTypeGroup && !item.isNeighborhood && item.spaceType !== 'object') {
            this.mapService.select(this.mapSpaceSelected, false); // opens the info panel
        }
        // Only searching for a single space/object zooms; legend, group and type selections keep the current view
        if (item && source === 'search' && !item.isTypeGroup && !item.isNeighborhood) {
            this.mapService.zoomToSelection();
        }
    },
    palletObjectChanged(activePalletArray){
        if(this.palletObjectSelected){
            let obj = activePalletArray.find(o => o.id === this.palletObjectSelected);
            this.mapService.changeCreateObject(obj);
        } else { 
            this.mapService.changeCreateObject(null); 
        }
    },
    async renderActiveLegend() {
        if (this.legendService) {
            // Load settings for both potential legends
            await Promise.all([
                this.legendService.loadSettings(this.buildingMitId, this.selectedFloor, 'neighborhood'),
                this.legendService.loadSettings(this.buildingMitId, this.selectedFloor, 'conference')
            ]);
            
            this.legendService.renderLegend(
                this.groups, 
                this.spacesModel[this.selectedFloor], 
                this.drawing, 
                this.buildingMitId, 
                this.selectedFloor,
                this.showNeighborhoods,
                this.showConferenceRooms,
                {
                    activeGroupId: this.selectedGroupId,
                    activeSpaceId: this.selectionKind === 'space' ? this.mapSpaceSelected : null,
                    onSelect: (legendType, item) => this.legendItemClicked(legendType, item),
                    onHover: (legendType, item) => this.legendItemHovered(legendType, item)
                }
            );
        }
    },
    drawMap() {
        this.mapService.setMapDisplayOptions(this.mapType, this.showAllObjects, this.showNeighborhoods, this.showConferenceRooms);
        this.mapService.setRoomNumberDisplayOptions(this.roomNumberOptions);
        // Fading the rest of the floor gets in the way while editing
        this.mapService.setSelectionDimming(!this.drawing);
        

        let featuresToDraw = [];
        if (this.showNavigation || (this.drawing && this.activeTab === 2)) {
            featuresToDraw = this.navFeaturesModel.filter(f => {
                // 1. Existing check for room entrances
                if (!this.showEntrances && f.properties.useType === 'NAV_ROOM_ENTRANCE') {
                    return false;
                }

                // 2. Handle LEVEL_CHANGE vertical nodes
                if (f.properties.useType === 'LEVEL_CHANGE') {
                    const accessLevels = f.properties.accessToLevels || [];
                    
                    if (accessLevels.includes(this.selectedFloor)) {
                        // Re-seed the level so the drawing engine's strict validation passes
                        f.properties.level = this.selectedFloor;
                        return true;
                    } else {
                        // The catch: It's an elevator/stair node, but it doesn't have access to this floor.
                        // If its base level property somehow matches the floor we are on, but it's not in the access array, that's a data mismatch.
                        if (f.properties.level === this.selectedFloor) {
                            console.warn(`[Map Diagnostics] LEVEL_CHANGE node ${f.properties.mitId} has a base level of ${this.selectedFloor}, but this floor is missing from its accessToLevels array. Excluding from render.`);
                        }
                        // Filter it out so it doesn't ghost-render on a floor it doesn't belong to
                        return false; 
                    }
                }

                // Let all other standard points/paths through normally
                return true;
            });
        }

        this.mapService.createMap(
            this.spacesModel, this.objectsModel, this.doorsModel, this.windowsModel, 
            this.structuresModel, this.info.floorDict, this.size.w, this.size.h, 
            this.selectedFloor, this.selectedType, this.mapSpaceSelected, 
            this.selectedOrg, featuresToDraw, this.selectedGroupId
        );
        
        this.renderActiveLegend();
    },
    onCsailOnlyChange() {
        if (this.showCsailOnly) {
            this.selectedOrg = 'COMP SCI & AI LAB';
        } else {
            this.selectedOrg = null;
        }
        this.drawMap();
    },
    downloadSVG() {
        const svgElement = document.getElementById('mit-svg');
        if (!svgElement) return;

        const serializer = new XMLSerializer();
        let source = serializer.serializeToString(svgElement);

        if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) { source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"'); }
        if (!source.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) { source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'); }

        source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
        const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);

        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `Building_${this.buildingMitId}_Floor_${this.selectedFloor}.svg`;
        
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    },
    uploadSpaceImage(spaceId){
        this.selectedSpaceImageToUploadLoading = true;
          MitSpacesService.uploadSpaceImage(spaceId, this.selectedSpaceImageToUpload, this.selectedSpace.properties.images.length)
              .then( (data)=> {
                  this.selectedSpace.properties.images.push(data.filename);
                  this.editedSelectedSpaceProperties();
                  this.selectedSpaceImageToUploadLoading = false;
                  this.selectedSpaceImageToUpload = null;
              })
              .catch( (error)=> {
                  this.selectedSpaceImageToUploadLoading = false;
                  console.log('An error occurred', error);
              });
      },
      deleteSpaceImage(imageName, index){
          MitSpacesService.deleteSpaceImage(imageName)
              .then( (data)=> {
                  this.editedSelectedSpaceProperties();
                  this.selectedSpace.properties.images.splice(index, 1);
              })
              .catch( (error)=> { console.log('An error occurred', error); })
      },
      
      async openPrintableQrCode() {
          const mitId = this.selectedSpace.properties.mitId;
          const targetUrl = `${window.location.origin}/directions?qr=${mitId}`;
          
          try {
              await fetch('/api/v2/manager/spaces/navigation/qr', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ mitId: mitId, url: targetUrl })
              });
              
              const routeData = this.$router.resolve({ 
                  path: '/manage/print-qr', 
                  query: { 
                      id: mitId,
                      name: this.selectedSpace.properties.name || this.selectedSpace.properties.mitIdShort || 'Navigation Point'
                  } 
              });
              window.open(routeData.href, '_blank');
          } catch (err) {
              console.error("Failed to generate QR code", err);
              alert("Failed to generate QR code on the server.");
          }
      }
  }
}
</script>

<style lang="scss" scoped>
  .mit-spaces-map-edit {
    position: relative; /* Restricts the map inside its container */
    height: calc(100vh - 160px); /* Gives the map proper height to fill the screen while accounting for headers */
    width: 100%;
    overflow: hidden; /* Stops page-level scrolling */
  }
  .map-container-card {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden;
    height: 100%;
  }
  
  /* Top Right */
  .search-container {
    position: absolute;
    top: 10px; 
    right: 10px; 
    z-index: 5;
    max-width: 400px;
    width: 100%;
  }
  .search-card { width: 100%; }
  .search-input { ::v-deep .v-input__control .v-input__slot { box-shadow: none !important; } }
  
  .scrollable {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden; 
  }
  svg#mit-svg { width: 100%; height: 100%; display: block; }
  
  /* Top Left */
  .toolbar-drawing-left {
      position: absolute;
      top: 10px; left: 10px; /* Explicit Top Left */
      width: 320px; z-index: 5;
      pointer-events: none; 
  }
  .toolbar-drawing-left .v-card,
  .toolbar-drawing-left .v-btn { pointer-events: auto; }
  
  /* Center Topish */
  .toolbar-drawing-center {
      position: absolute;
      top: 10px; left: calc(50% - 100px);
      width: 200px; z-index: 5;
  }
  
  /* Right Side Area (Below Search) */
  .toolbar-drawing-right {
      position: absolute;
      top: 80px; right: 10px;
      width: 300px; z-index: 5;
  }
  .space-props-card {
      max-height: calc(100vh - 280px);
  }

  /* Bottom Right Zoom Controls */
  .toolbar-zoom-controls {
      position: absolute;
      bottom: 20px; right: 10px;
      z-index: 5;
      display: flex;
      flex-direction: column;
      gap: 8px;
  }

  /* Bottom Left Map View Settings */
  .toolbar-map-types {
      position: absolute;
      bottom: 20px; left: 10px;
      z-index: 5;
  }

  /* Responsive Adjustments for Mobile Layouts */
  @media (max-width: 600px) {
      .toolbar-zoom-controls { display: none !important; }
      .toolbar-drawing-right {
          top: auto !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
      }
      .space-props-card {
          max-height: 45vh !important;
          border-bottom-left-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
      }
      .search-container {
          left: 10px; /* span edge-to-edge on mobile */
          max-width: 100%; 
      }
  }
</style>